import React, { useState, useEffect } from 'react';
import { superAdminAPI, companyAPI } from '../services/api';
import Icon from './Icon';

function SuperAdminBikes({ companies: companiesProp = [], darkMode = true }) {
  const [bikes, setBikes] = useState([]);
  const [companies, setCompanies] = useState(companiesProp);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [companyFilter, setCompanyFilter] = useState('');
  const [stolenFilter, setStolenFilter] = useState('all');
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const perPage = 50;

  // Detail modal
  const [selectedBike, setSelectedBike] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [bikeDetail, setBikeDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Transfer modal
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferRecipient, setTransferRecipient] = useState('');
  const [transferring, setTransferring] = useState(false);

  // Service edit modal
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [serviceForm, setServiceForm] = useState({
    description: '',
    total_cost: '',
    service_date: '',
    status: 'completed',
  });
  const [savingService, setSavingService] = useState(false);

  useEffect(() => {
    if (companiesProp.length === 0) {
      fetchCompanies();
    } else {
      setCompanies(companiesProp);
    }
  }, [companiesProp]);

  useEffect(() => {
    fetchBikes();
  }, [companyFilter, page]);

  const fetchCompanies = async () => {
    try {
      const response = await companyAPI.getAllCompanies();
      setCompanies(response.data.companies || []);
    } catch (err) {
      console.error('Failed to load companies:', err);
    }
  };

  const fetchBikes = async () => {
    try {
      setLoading(true);
      const params = { page, per_page: perPage };
      if (companyFilter) params.company_id = companyFilter;
      const response = await superAdminAPI.getAllBikes(params);
      setBikes(response.data.bikes || []);
      setTotal(response.data.total || 0);
      setPages(response.data.pages || 1);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load bikes');
    } finally {
      setLoading(false);
    }
  };

  const openDetail = async (bike) => {
    setSelectedBike(bike);
    setShowDetailModal(true);
    setLoadingDetail(true);
    try {
      const response = await superAdminAPI.getBike(bike.id);
      setBikeDetail(response.data.bike);
    } catch (err) {
      setError('Failed to load bike details');
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleToggleStolen = async (bike) => {
    const newStolen = !bike.is_stolen;
    const action = newStolen ? 'mark as stolen/blocked' : 'unblock';
    if (!window.confirm(`Are you sure you want to ${action} this bike (${bike.brand} ${bike.model})?`)) return;

    try {
      await superAdminAPI.updateBike(bike.id, { is_stolen: newStolen });
      setSuccess(`Bike ${newStolen ? 'blocked (marked as stolen)' : 'unblocked'} successfully`);
      fetchBikes();
      if (bikeDetail && bikeDetail.id === bike.id) {
        setBikeDetail(prev => ({ ...prev, is_stolen: newStolen }));
      }
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update bike');
    }
  };

  const handleTransfer = async (e) => {
    e.preventDefault();
    if (!transferRecipient.trim() || !selectedBike) return;
    setTransferring(true);
    try {
      const response = await superAdminAPI.transferBike(selectedBike.id, transferRecipient.trim());
      const count = response.data.transferred_bookings || 0;
      let msg = `Bike transferred to ${transferRecipient}!`;
      if (count > 0) msg += ` ${count} booking(s) also transferred.`;
      setSuccess(msg);
      setShowTransferModal(false);
      setTransferRecipient('');
      fetchBikes();
      if (showDetailModal) {
        const resp = await superAdminAPI.getBike(selectedBike.id);
        setBikeDetail(resp.data.bike);
      }
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(err.response?.data?.error || 'Transfer failed');
    } finally {
      setTransferring(false);
    }
  };

  const handleDelete = async (bike) => {
    if (!window.confirm(`PERMANENTLY DELETE bike ${bike.brand} ${bike.model} (VIN: ${bike.vin})? This cannot be undone!`)) return;
    try {
      await superAdminAPI.deleteBike(bike.id);
      setSuccess('Bike deleted');
      setShowDetailModal(false);
      fetchBikes();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete bike');
    }
  };

  const reloadBikeDetail = async () => {
    if (!selectedBike) return;
    try {
      const resp = await superAdminAPI.getBike(selectedBike.id);
      setBikeDetail(resp.data.bike);
    } catch (err) {
      console.error('Failed to reload bike detail:', err);
    }
  };

  const openServiceEdit = (service) => {
    setEditingService(service);
    const dateStr = service.service_date
      ? (typeof service.service_date === 'string'
          ? service.service_date.slice(0, 10)
          : new Date(service.service_date).toISOString().slice(0, 10))
      : '';
    setServiceForm({
      description: service.description || '',
      total_cost: service.total_cost != null ? String(service.total_cost) : '',
      service_date: dateStr,
      status: service.status || 'completed',
    });
    setShowServiceModal(true);
  };

  const handleUpdateService = async (e) => {
    e.preventDefault();
    if (!editingService) return;
    setSavingService(true);
    try {
      const payload = {
        description: serviceForm.description,
        status: serviceForm.status,
      };
      if (serviceForm.service_date) payload.service_date = serviceForm.service_date;
      if (serviceForm.total_cost !== '') {
        const cost = parseFloat(serviceForm.total_cost);
        if (Number.isNaN(cost)) {
          setError('Total cost must be a number');
          setSavingService(false);
          return;
        }
        payload.total_cost = cost;
      }
      await superAdminAPI.updateService(editingService.id, payload);
      setSuccess('Service updated');
      setShowServiceModal(false);
      setEditingService(null);
      await reloadBikeDetail();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update service');
    } finally {
      setSavingService(false);
    }
  };

  const handleDeleteService = async (service) => {
    if (!window.confirm(`Permanently delete this service record?\n\n"${service.description || 'Service'}" (${service.status})\n\nThis cannot be undone.`)) return;
    try {
      await superAdminAPI.deleteService(service.id);
      setSuccess('Service deleted');
      await reloadBikeDetail();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete service');
    }
  };

  // Client-side search + stolen filter
  const filtered = bikes.filter(b => {
    const term = searchTerm.toLowerCase();
    const matchSearch = !term || 
      (b.vin || '').toLowerCase().includes(term) ||
      (b.brand || '').toLowerCase().includes(term) ||
      (b.model || '').toLowerCase().includes(term) ||
      String(b.id).includes(term) ||
      String(b.owner_id || '').includes(term);
    const matchStolen = stolenFilter === 'all' || 
      (stolenFilter === 'stolen' && b.is_stolen) ||
      (stolenFilter === 'active' && !b.is_stolen);
    return matchSearch && matchStolen;
  });

  const getCompanyName = (companyId) => {
    if (!companyId) return '-';
    const c = companies.find(co => co.id === companyId);
    return c ? c.name : `Company #${companyId}`;
  };

  if (loading && bikes.length === 0) {
    return <div className="loading">Loading bikes...</div>;
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <h2 style={{ color: darkMode ? '#ffffff' : '#1a1a1a', margin: 0, fontSize: '18px', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Icon name="bike" color="brand" darkMode={darkMode} size={20} />
          All Bikes ({total})
        </h2>
        <button onClick={fetchBikes} className="btn btn-secondary" style={{ fontSize: '13px' }}>
          <Icon name="refresh" color="secondary" darkMode={darkMode} size={14} style={{ marginRight: '4px' }} />
          Refresh
        </button>
      </div>

      {error && <div className="error" style={{ marginBottom: '16px' }}>{error}</div>}
      {success && <div className="success" style={{ marginBottom: '16px', padding: '12px', background: 'rgba(16, 185, 129, 0.15)', border: '1px solid rgba(16, 185, 129, 0.3)', borderRadius: '12px', color: '#4A8C18' }}>{success}</div>}

      {/* Filters */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <input
          type="text"
          placeholder="Search VIN, brand, model, ID..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ flex: 1, minWidth: '180px', padding: '10px 14px', borderRadius: '10px', border: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`, background: darkMode ? 'rgba(255,255,255,0.05)' : '#fff', color: darkMode ? '#fff' : '#1a1a1a', fontSize: '14px' }}
        />
        <select
          value={companyFilter}
          onChange={(e) => { setCompanyFilter(e.target.value); setPage(1); }}
          style={{ padding: '10px 14px', borderRadius: '10px', border: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`, background: darkMode ? 'rgba(255,255,255,0.05)' : '#fff', color: darkMode ? '#fff' : '#1a1a1a', fontSize: '14px', minWidth: '140px' }}
        >
          <option value="">All Companies</option>
          {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select
          value={stolenFilter}
          onChange={(e) => setStolenFilter(e.target.value)}
          style={{ padding: '10px 14px', borderRadius: '10px', border: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`, background: darkMode ? 'rgba(255,255,255,0.05)' : '#fff', color: darkMode ? '#fff' : '#1a1a1a', fontSize: '14px', minWidth: '120px' }}
        >
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="stolen">Stolen/Blocked</option>
        </select>
      </div>

      {/* Bikes list - card layout for mobile */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1px', borderRadius: '16px', overflow: 'hidden', border: `1px solid ${darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.08)'}` }}>
        {filtered.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: darkMode ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)', background: darkMode ? 'rgba(255,255,255,0.02)' : '#fff' }}>
            No bikes found
          </div>
        ) : filtered.map((bike, idx) => (
          <div key={bike.id} style={{
            padding: '14px 16px',
            background: darkMode
              ? (bike.is_stolen ? 'rgba(239, 68, 68, 0.08)' : (idx % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.04)'))
              : (bike.is_stolen ? 'rgba(239, 68, 68, 0.05)' : (idx % 2 === 0 ? '#fff' : '#fafafa')),
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            cursor: 'pointer',
            transition: 'background 0.15s'
          }}
            onClick={() => openDetail(bike)}
            onMouseOver={(e) => { e.currentTarget.style.background = darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.03)'; }}
            onMouseOut={(e) => { e.currentTarget.style.background = darkMode ? (bike.is_stolen ? 'rgba(239, 68, 68, 0.08)' : (idx % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.04)')) : (bike.is_stolen ? 'rgba(239, 68, 68, 0.05)' : (idx % 2 === 0 ? '#fff' : '#fafafa')); }}
          >
            {/* Row 1: Brand Model + Status */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Icon name="bike" color={bike.is_stolen ? "error" : "brand"} darkMode={darkMode} size={18} />
                <strong style={{ fontSize: '15px', color: darkMode ? '#fff' : '#1a1a1a' }}>
                  {bike.brand} {bike.model}
                </strong>
                {bike.year && <span style={{ fontSize: '12px', color: darkMode ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)' }}>({bike.year})</span>}
              </div>
              <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                {bike.is_stolen && (
                  <span style={{ padding: '3px 8px', borderRadius: '8px', fontSize: '10px', fontWeight: '700', background: 'rgba(239, 68, 68, 0.2)', color: '#ef4444', textTransform: 'uppercase' }}>
                    Blocked
                  </span>
                )}
                <span style={{ fontSize: '11px', color: darkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)' }}>
                  #{bike.id}
                </span>
              </div>
            </div>
            {/* Row 2: VIN, Owner, Company */}
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', fontSize: '12px', color: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)' }}>
              <span><strong>VIN:</strong> {bike.vin}</span>
              <span><strong>Owner:</strong> {bike.owner_id ? `User #${bike.owner_id}` : 'Unassigned'}</span>
              <span><strong>Company:</strong> {getCompanyName(bike.company_id)}</span>
              {bike.created_at && <span><strong>Added:</strong> {new Date(bike.created_at).toLocaleDateString()}</span>}
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      {pages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '20px', flexWrap: 'wrap' }}>
          <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="btn btn-secondary btn-sm" style={{ minWidth: '80px' }}>Previous</button>
          <span style={{ padding: '8px 16px', fontSize: '13px', color: darkMode ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)' }}>
            Page {page} of {pages} ({total} bikes)
          </span>
          <button disabled={page >= pages} onClick={() => setPage(p => p + 1)} className="btn btn-secondary btn-sm" style={{ minWidth: '80px' }}>Next</button>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedBike && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }} onClick={() => setShowDetailModal(false)}>
          <div style={{ background: darkMode ? '#1a1a3e' : '#fff', borderRadius: '20px', padding: '28px', maxWidth: '600px', width: '100%', maxHeight: '85vh', overflowY: 'auto', color: darkMode ? '#fff' : '#1a1a1a' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, fontSize: '18px' }}>
                <Icon name="bike" color="brand" darkMode={darkMode} size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                Bike Details
              </h2>
              <button onClick={() => setShowDetailModal(false)} style={{ background: 'none', border: 'none', color: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)', fontSize: '24px', cursor: 'pointer' }}>x</button>
            </div>

            {loadingDetail ? (
              <div style={{ padding: '40px', textAlign: 'center', color: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)' }}>Loading...</div>
            ) : bikeDetail ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {/* Basic Info */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px' }}>
                  {[
                    { label: 'ID', value: bikeDetail.id },
                    { label: 'VIN', value: bikeDetail.vin },
                    { label: 'Brand', value: bikeDetail.brand },
                    { label: 'Model', value: bikeDetail.model },
                    { label: 'Year', value: bikeDetail.year },
                    { label: 'Owner ID', value: bikeDetail.owner_id || 'None' },
                    { label: 'Company', value: getCompanyName(bikeDetail.company_id) },
                    { label: 'Added by', value: bikeDetail.added_by_user_id ? `User #${bikeDetail.added_by_user_id}` : '-' },
                    { label: 'Created', value: bikeDetail.created_at ? new Date(bikeDetail.created_at).toLocaleString() : '-' },
                    { label: 'Updated', value: bikeDetail.updated_at ? new Date(bikeDetail.updated_at).toLocaleString() : '-' },
                  ].map(({ label, value }) => (
                    <div key={label} style={{ padding: '10px', background: darkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)', borderRadius: '10px' }}>
                      <div style={{ fontSize: '10px', color: darkMode ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)', textTransform: 'uppercase', fontWeight: '600', marginBottom: '4px' }}>{label}</div>
                      <div style={{ fontSize: '14px', fontWeight: '600', wordBreak: 'break-all' }}>{value}</div>
                    </div>
                  ))}
                </div>

                {/* Status */}
                <div style={{ padding: '14px', borderRadius: '12px', background: bikeDetail.is_stolen ? 'rgba(239,68,68,0.1)' : 'rgba(34,197,94,0.08)', border: `1px solid ${bikeDetail.is_stolen ? 'rgba(239,68,68,0.2)' : 'rgba(34,197,94,0.15)'}` }}>
                  <div style={{ fontSize: '13px', fontWeight: '700', color: bikeDetail.is_stolen ? '#ef4444' : '#22c55e', marginBottom: '4px' }}>
                    {bikeDetail.is_stolen ? 'BLOCKED / Stolen' : 'Active'}
                  </div>
                  {bikeDetail.is_stolen && bikeDetail.stolen_reported_at && (
                    <div style={{ fontSize: '11px', color: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)' }}>
                      Reported: {new Date(bikeDetail.stolen_reported_at).toLocaleString()}
                    </div>
                  )}
                </div>

                {/* Notes / Description */}
                {(bikeDetail.notes || bikeDetail.description) && (
                  <div style={{ padding: '12px', background: darkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)', borderRadius: '10px' }}>
                    {bikeDetail.notes && <div style={{ fontSize: '13px', marginBottom: bikeDetail.description ? '8px' : 0 }}><strong>Notes:</strong> {bikeDetail.notes}</div>}
                    {bikeDetail.description && <div style={{ fontSize: '13px' }}><strong>Description:</strong> {bikeDetail.description}</div>}
                  </div>
                )}

                {/* Services */}
                {bikeDetail.services && bikeDetail.services.length > 0 && (
                  <div>
                    <h4 style={{ fontSize: '13px', color: darkMode ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)', marginBottom: '8px' }}>Services ({bikeDetail.services.length})</h4>
                    <div style={{ maxHeight: '260px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {bikeDetail.services.map(s => (
                        <div key={s.id} style={{ padding: '10px 12px', background: darkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)', borderRadius: '8px', fontSize: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: '600', color: darkMode ? '#fff' : '#1a1a1a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {s.description || 'Service'}
                            </div>
                            <div style={{ fontSize: '11px', color: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)', marginTop: '2px' }}>
                              #{s.id} • {s.status}
                              {s.service_date && ` • ${typeof s.service_date === 'string' ? s.service_date.slice(0, 10) : new Date(s.service_date).toLocaleDateString()}`}
                            </div>
                          </div>
                          <span style={{ fontWeight: '600', color: '#22c55e', whiteSpace: 'nowrap' }}>
                            {s.total_cost ? `£${parseFloat(s.total_cost).toFixed(2)}` : '-'}
                          </span>
                          <div style={{ display: 'flex', gap: '4px' }}>
                            <button
                              onClick={() => openServiceEdit(s)}
                              title="Edit service"
                              style={{ width: '32px', height: '32px', background: darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)', border: 'none', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            >
                              <Icon name="edit" color="secondary" darkMode={darkMode} size={14} />
                            </button>
                            <button
                              onClick={() => handleDeleteService(s)}
                              title="Delete service"
                              style={{ width: '32px', height: '32px', background: 'rgba(239,68,68,0.12)', border: 'none', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            >
                              <Icon name="delete" color="error" darkMode={darkMode} size={14} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Company Registrations */}
                {bikeDetail.company_registrations && bikeDetail.company_registrations.length > 0 && (
                  <div>
                    <h4 style={{ fontSize: '13px', color: darkMode ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)', marginBottom: '8px' }}>Company Registrations ({bikeDetail.company_registrations.length})</h4>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                      {bikeDetail.company_registrations.map(reg => (
                        <span key={reg.id} style={{ padding: '4px 10px', borderRadius: '8px', fontSize: '11px', background: reg.is_active ? 'rgba(59,130,246,0.12)' : 'rgba(255,255,255,0.05)', color: reg.is_active ? '#60a5fa' : 'rgba(255,255,255,0.3)' }}>
                          {getCompanyName(reg.company_id)} {reg.is_active ? '' : '(inactive)'}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', paddingTop: '8px', borderTop: `1px solid ${darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}` }}>
                  <button
                    onClick={() => handleToggleStolen(bikeDetail)}
                    className={bikeDetail.is_stolen ? 'btn btn-primary' : 'btn btn-danger'}
                    style={{ flex: 1, minWidth: '120px', minHeight: '44px', fontSize: '13px' }}
                  >
                    <Icon name={bikeDetail.is_stolen ? 'success' : 'error'} darkMode={darkMode} size={14} style={{ marginRight: '6px' }} />
                    {bikeDetail.is_stolen ? 'Unblock Bike' : 'Block Bike'}
                  </button>
                  <button
                    onClick={() => { setShowTransferModal(true); }}
                    className="btn btn-secondary"
                    style={{ flex: 1, minWidth: '120px', minHeight: '44px', fontSize: '13px' }}
                  >
                    <Icon name="swap_horiz" color="secondary" darkMode={darkMode} size={14} style={{ marginRight: '6px' }} />
                    Transfer
                  </button>
                  <button
                    onClick={() => handleDelete(bikeDetail)}
                    style={{ minWidth: '44px', minHeight: '44px', background: 'rgba(239,68,68,0.12)', color: '#ef4444', border: 'none', borderRadius: '10px', cursor: 'pointer', fontSize: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    title="Delete bike permanently"
                  >
                    <Icon name="delete" color="error" darkMode={darkMode} size={16} />
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      )}

      {/* Transfer Modal */}
      {showTransferModal && selectedBike && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 10001, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }} onClick={() => setShowTransferModal(false)}>
          <div style={{ background: darkMode ? '#1a1a3e' : '#fff', borderRadius: '20px', padding: '28px', maxWidth: '420px', width: '100%', color: darkMode ? '#fff' : '#1a1a1a' }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 16px', fontSize: '16px' }}>
              Transfer Bike #{selectedBike.id}
            </h3>
            <p style={{ fontSize: '13px', color: darkMode ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)', marginBottom: '16px' }}>
              {selectedBike.brand} {selectedBike.model} — Current owner: User #{selectedBike.owner_id || 'None'}
            </p>
            <form onSubmit={handleTransfer}>
              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label style={{ fontSize: '13px', marginBottom: '6px', display: 'block' }}>Recipient (username or email)</label>
                <input
                  type="text"
                  value={transferRecipient}
                  onChange={(e) => setTransferRecipient(e.target.value)}
                  placeholder="username or email@example.com"
                  required
                  style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`, background: darkMode ? 'rgba(255,255,255,0.06)' : '#f9f9f9', color: darkMode ? '#fff' : '#1a1a1a', fontSize: '14px' }}
                />
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button type="submit" disabled={transferring} className="btn btn-primary" style={{ flex: 1, minHeight: '44px' }}>
                  {transferring ? 'Transferring...' : 'Transfer'}
                </button>
                <button type="button" onClick={() => setShowTransferModal(false)} className="btn btn-secondary" style={{ flex: 1, minHeight: '44px' }}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Service Modal */}
      {showServiceModal && editingService && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 10002, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }} onClick={() => setShowServiceModal(false)}>
          <div style={{ background: darkMode ? '#1a1a3e' : '#fff', borderRadius: '20px', padding: '28px', maxWidth: '480px', width: '100%', maxHeight: '85vh', overflowY: 'auto', color: darkMode ? '#fff' : '#1a1a1a' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '16px' }}>
                Edit Service #{editingService.id}
              </h3>
              <button onClick={() => setShowServiceModal(false)} style={{ background: 'none', border: 'none', color: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)', fontSize: '22px', cursor: 'pointer' }}>x</button>
            </div>
            <form onSubmit={handleUpdateService} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div>
                <label style={{ fontSize: '12px', marginBottom: '4px', display: 'block', color: darkMode ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)' }}>Description</label>
                <textarea
                  value={serviceForm.description}
                  onChange={(e) => setServiceForm(prev => ({ ...prev, description: e.target.value }))}
                  rows={4}
                  style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`, background: darkMode ? 'rgba(255,255,255,0.06)' : '#f9f9f9', color: darkMode ? '#fff' : '#1a1a1a', fontSize: '14px', fontFamily: 'inherit', resize: 'vertical' }}
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                <div>
                  <label style={{ fontSize: '12px', marginBottom: '4px', display: 'block', color: darkMode ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)' }}>Total cost (£)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={serviceForm.total_cost}
                    onChange={(e) => setServiceForm(prev => ({ ...prev, total_cost: e.target.value }))}
                    style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`, background: darkMode ? 'rgba(255,255,255,0.06)' : '#f9f9f9', color: darkMode ? '#fff' : '#1a1a1a', fontSize: '14px', boxSizing: 'border-box' }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: '12px', marginBottom: '4px', display: 'block', color: darkMode ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)' }}>Service date</label>
                  <input
                    type="date"
                    value={serviceForm.service_date}
                    onChange={(e) => setServiceForm(prev => ({ ...prev, service_date: e.target.value }))}
                    style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`, background: darkMode ? 'rgba(255,255,255,0.06)' : '#f9f9f9', color: darkMode ? '#fff' : '#1a1a1a', fontSize: '14px', boxSizing: 'border-box' }}
                  />
                </div>
              </div>
              <div>
                <label style={{ fontSize: '12px', marginBottom: '4px', display: 'block', color: darkMode ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)' }}>Status</label>
                <select
                  value={serviceForm.status}
                  onChange={(e) => setServiceForm(prev => ({ ...prev, status: e.target.value }))}
                  style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`, background: darkMode ? 'rgba(255,255,255,0.06)' : '#f9f9f9', color: darkMode ? '#fff' : '#1a1a1a', fontSize: '14px' }}
                >
                  <option value="scheduled">Scheduled</option>
                  <option value="in_progress">In progress</option>
                  <option value="ready_for_billing">Ready for billing</option>
                  <option value="pending_payment">Pending payment</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              <div style={{ display: 'flex', gap: '10px', marginTop: '4px' }}>
                <button type="submit" disabled={savingService} className="btn btn-primary" style={{ flex: 1, minHeight: '44px' }}>
                  {savingService ? 'Saving...' : 'Save changes'}
                </button>
                <button type="button" onClick={() => setShowServiceModal(false)} className="btn btn-secondary" style={{ flex: 1, minHeight: '44px' }}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default SuperAdminBikes;
